import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, combineLatest, finalize, map, Observable } from 'rxjs';

export type ImportUploadHandler = (file: File) => Observable<any>;
export type ImportUploadSuccessHandler = (response: any) => void;

@Component({
  selector: 'app-import-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './import-file-upload.component.html',
  styleUrl: './import-file-upload.component.scss'
})
export class ImportFileUploadComponent {
  @Input() entityLabel = 'registros';
  @Input() uploadHandler?: ImportUploadHandler;
  @Input() uploadSuccessHandler?: ImportUploadSuccessHandler;

  selectedFile: File | null = null;
  selectedFileName = '';
  validationError = '';
  loading = false;
  resultsSource = new BehaviorSubject<any[]>([]);
  results$ = this.resultsSource.asObservable();
  hasResults$ = this.results$.pipe(map(results => results.length > 0));

  activeFilterSubject = new BehaviorSubject<'all' | 'success' | 'error'>('all');
  activeFilter$ = this.activeFilterSubject.asObservable();

  sumaryResult$ = this.results$.pipe(map(results => [
    { label: 'correctos', count: results.filter((item) => this.isSuccessfulResult(item)).length, filter: 'success', styleClass: 'summary-pill success' },
    { label: 'totales', count: results.length, filter: 'all', styleClass: 'summary-pill neutral' },
    { label: 'con novedad', count: results.filter((item) => !this.isSuccessfulResult(item)).length, filter: 'error', styleClass: 'summary-pill danger' }
  ]));

  visibleResults$ = combineLatest([this.results$, this.activeFilter$]).pipe(
    map(([results, activeFilter]) => {
      if (activeFilter === 'all') {
        return results;
      } else if (activeFilter === 'success') {
        return results.filter((item) => this.isSuccessfulResult(item));
      } else if (activeFilter === 'error') {
        return results.filter((item) => !this.isSuccessfulResult(item));
      }
      return results;
    })
  );

  constructor(private dialogRef: MatDialogRef<ImportFileUploadComponent>) {}

  get showDashboardWarning(): boolean {
    return this.entityLabel === 'accesos del dashboard';
  }

  showingResults(): boolean {
    let hasResults = false;
    this.results$.subscribe(results => {
      hasResults = results.length > 0;
    });
    return hasResults;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.validationError = '';
    this.selectedFile = null;
    this.selectedFileName = '';
    this.resultsSource.next([]);

    if (!file) {
      return;
    }

    const lowerFileName = file.name.toLowerCase();
    const hasValidExtension = lowerFileName.endsWith('.csv') || lowerFileName.endsWith('.xlsx');
    if (!hasValidExtension) {
      this.validationError = 'Solo se permiten archivos .csv y .xlsx.';
      return;
    }

    this.selectedFile = file;
    this.selectedFileName = file.name;
  }

  uploadFile(): void {
    if (!this.selectedFile || this.loading || !this.uploadHandler) {
      return;
    }

    this.loading = true;
    this.validationError = '';
    this.resultsSource.next([]);

    this.uploadHandler(this.selectedFile)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe({
        next: (response) => {
          this.resultsSource.next(Array.isArray(response?.results) ? response.results : []);
          this.uploadSuccessHandler?.(response);
        },
        error: () => {
          this.validationError = 'No se pudo importar el archivo.';
        }
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  resetImport(): void {
    this.resultsSource.next([]);
    this.validationError = '';
    this.selectedFile = null;
    this.selectedFileName = '';
  }

  formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^\w/, (char) => char.toUpperCase());
  }

  getVisibleEntries(item: Record<string, unknown>): Array<[string, unknown]> {
    return Object.entries(item ?? {}).filter(([, value]) => value !== null && value !== undefined && value !== '');
  }

  isPrimitive(value: unknown): boolean {
    return value === null || ['string', 'number', 'boolean'].includes(typeof value);
  }

  isObjectLike(value: unknown): boolean {
    return typeof value === 'object' && value !== null;
  }

  formatValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.every((item) => this.isPrimitive(item))
        ? value.join(', ')
        : JSON.stringify(value, null, 2);
    }

    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  trackByIndex(index: number): number {
    return index;
  }

  isSuccessfulResult(item: any): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    if (typeof item.estado === 'string' && item.estado === 'invalido' || item.estado === 'fallido') {
      return false;
    }

    return true;
  }

  onChangeFilter(event: any) {
    this.activeFilterSubject.next(event);
  }
}
